import { useCallback, useEffect, useMemo, useState } from "react";
import { parseAmount } from "../../services/utils";
import styles from "./Table.module.css";

function BookingsLedgerTable({ data, bookingData }) {
  const { paymentType, emiCount, emiValue } = bookingData;
  const ledgerColumns = useMemo(
    () => [
      { id: "date", name: "Due/date received" },
      { id: "payment_type", name: "Amount Type" },
      { id: "amount_due", name: "Amount Due" },
      {
        id: paymentType === "emi" ? "emi_amount" : "down_payment",
        name: paymentType === "emi" ? "EMI" : "Down Payment",
      },
      { id: "waive_off_amount", name: "Waive Off" },
      { id: "agent_commission", name: "Agent commission" },
      { id: "company_commission", name: "Company commission" },
    ],
    [paymentType]
  );

  const [tableData, setTableData] = useState([]);

  const parseDate = (date) => {
    if (!date) return "-";
    const dateObj = new Date(date);
    return `${dateObj.getDate()}/${dateObj.getMonth() + 1}/${dateObj.getFullYear()}`;
  };

  const paymentTypeName = useMemo(
    () => ({
      token: "Token",
      downpayment: "Down Payment",
      registrationcharges: "Registration Charges",
      developmentcharges: "Development Charges",
      emi: "EMI",
      booking_advance: "Booking Advance",
    }),
    []
  );

  const formatLedgerData = useCallback(() => {
    if (!data || data.length === 0) return [];
    let formattedLedgerData = [];
    formattedLedgerData = data.map((row) => ({
      ...row,
      id: row.payment_id,
      date_due: parseDate(row.date_due),
      date_received: parseDate(row.date_received),
      amount_due:
        row.payment_type === "emi"
          ? parseAmount(emiValue)
          : row.payment_type === "downpayment"
          ? parseAmount(+`${row.down_payment}`.replaceAll(",", "") + +`${row.waive_off_amount}`.replaceAll(",", ""))
          : parseAmount(row.amount_due),
      company_commission: row.payment_status !== "approve" ? "-" : parseAmount(row.company_commission),
      payment_type: paymentTypeName[row.payment_type.toLowerCase()] ?? row.payment_type,
      agent_commission: row.payment_status !== "approve" ? "-" : parseAmount(row.agent_commission),
      emi_amount: row.payment_status !== "approve" ? "-" : parseAmount(row.emi_amount),
      down_payment: row.payment_status !== "approve" ? "-" : parseAmount(row.down_payment),
      waive_off_amount: row.payment_status !== "approve" ? "-" : parseAmount(row.waive_off_amount),
      amount_paid:
        row.payment_status !== "approve"
          ? "-"
          : row.payment_type === "emi"
          ? parseAmount(row.emi_amount)
          : row.payment_type === "downpayment"
          ? parseAmount(row.down_payment)
          : parseAmount(`+${row.amount_due}`.replaceAll(",", "") - +`${row.waive_off_amount}`.replaceAll(",", "")),
      amount:
        row.payment_status !== "approve"
          ? 0
          : row.payment_type === "emi"
          ? +row.emi_amount.replaceAll(",", "")
          : row.payment_type === "downpayment"
          ? +row.down_payment.replaceAll(",", "")
          : +row.amount_due.replaceAll(",", "") - +row.waive_off_amount.replaceAll(",", ""),
    }));
    const isTokenPaid = !!formattedLedgerData.find((row) => row.payment_type === "Token");

    if (!isTokenPaid) {
      const bookingAdvanceDate = tableData.length
        ? new Date(
            tableData.find((row) => row.payment_type === "Booking Advance")?.date_received ??
              tableData[0]?.date_received
          )
        : new Date();
      const tokenRow = getUnpaidTokenRowData(bookingAdvanceDate);
      formattedLedgerData = [...formattedLedgerData.slice(0, 1), tokenRow, ...formattedLedgerData.slice(1)];
    }

    if (paymentType === "downpayment") {
      const isDownPaymentPaid = !!formattedLedgerData.find((row) => row.payment_type === "Down Payment");
      if (!isDownPaymentPaid) {
        const downPaymentRow = getUnpaidDownPaymentRowData();
        formattedLedgerData = [...formattedLedgerData, downPaymentRow];
      }
    }

    setTableData(formattedLedgerData);
  }, [data, emiValue, paymentTypeName]);

  const getUnpaidTokenRowData = (bookingDate) => {
    const tokenDate = new Date().setDate(bookingDate.getDate() + 15);
    const tokenDateStr = new Date(tokenDate).toISOString().split("T")[0];
    return {
      date_due: tokenDateStr.split("-").reverse().join("/"),
      amount_due: parseAmount(bookingData?.token),
      payment_type: "Token",
      payment_status: "unpaid",
      owner_payment: 0,
    };
  };

  const getUnpaidDownPaymentRowData = () => {
    return {
      date_due: "-",
      amount_due: parseAmount(bookingData?.downPayment),
      payment_type: "Down Payment",
      payment_status: "unpaid",
      owner_payment: 0,
    };
  };

  useEffect(() => {
    if (data) {
      formatLedgerData();
    }
  }, [data, formatLedgerData]);

  const generateEmiDates = (bookingDate) => {
    if (!bookingDate) return [];
    const startDate = new Date(bookingDate.split("/").reverse().join("/"));
    return Array.from({ length: emiCount }, (_, i) => {
      const emiDate = new Date(startDate);
      emiDate.setMonth(emiDate.getMonth() + i + 1);
      return `${emiDate.getDate()}/${emiDate.getMonth() + 1}/${emiDate.getFullYear()}`;
    });
  };

  const renderEmiRows = () => {
    if (paymentType !== "emi") return null;
    const bookingDate =
      tableData.find((row) => row.payment_type === "Booking Advance")?.date_received ?? tableData[0]?.date_received;

    const emiDatesArray = generateEmiDates(bookingDate);
    const paidEMIs = tableData.filter((row) => row.payment_type === "EMI");
    return Array.from({ length: emiCount }, (_, i) => {
      const paidEmi = paidEMIs[i];
      const isPaid = !!paidEmi;

      return (
        <div key={`emi-${i}`} className={styles.table_row}>
          {ledgerColumns.map((column) => {
            const value = isPaid
              ? column.id === "payment_type"
                ? `${paidEmi[column.id]} ${i + 1}`
                : column.id === "date"
                ? paidEmi.date_received || paidEmi.date_due
                : paidEmi[column.id]
              : column.id === "date"
              ? emiDatesArray[i]
              : column.id === "payment_type"
              ? `EMI ${i + 1}`
              : column.id === "amount_due"
              ? parseAmount(emiValue)
              : "-";

            const isHighlight =
              isPaid &&
              paidEmi.payment_status === "approve" &&
              !["date", "payment_type", "agent_commission", "company_commission"].includes(column.id) &&
              value !== "-";

            const isCommissionHighlight =
              ["agent_commission", "company_commission"].includes(column.id) && paidEmi?.owner_payment !== 0;

            return (
              <div key={column.id} className={styles.table_body_cell}>
                <span
                  className={
                    value === "-" || ["date", "payment_type"].includes(column.id)
                      ? ""
                      : isCommissionHighlight
                      ? styles.green
                      : isHighlight
                      ? styles.green
                      : styles.red
                  }
                >
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      );
    });
  };

  const renderTableRows = () =>
    tableData.map((row) => {
      if (row.payment_type === "EMI") return null;
      return (
        <div key={row.id} className={styles.table_row}>
          {ledgerColumns.map((column) => {
            let value = row[column.id] ?? "-";
            if (column.id === "date") {
              value = row.payment_status === "approve" && row.date_received ? row.date_received : row.date_due;
            }

            if (column.id === "emi_amount" && row.payment_type !== "EMI") {
              value = "-";
            }

            if (column.id === "down_payment" && row.payment_type !== "Down Payment") {
              value = "-";
            }

            if (column.id === "waive_off_amount" && !["EMI", "Down Payment"].includes(row.payment_type)) {
              value = ["EMI", "Down Payment"].includes(row.payment_type) ? row.waive_off_amount : "-";
            }

            const isHighlight =
              row.payment_status === "approve" &&
              !["date", "payment_type", "agent_commission", "company_commission"].includes(column.id) &&
              value !== "-";

            const isCommissionHighlight =
              (["agent_commission", "company_commission"].includes(column.id) && row.owner_payment !== 0) ||
              ["Development Charges", "Registration Charges"].includes(row.payment_type);

            return (
              <div key={column.id} className={styles.table_body_cell}>
                <span
                  className={
                    value === "-" || ["date", "payment_type"].includes(column.id)
                      ? ""
                      : isCommissionHighlight
                      ? styles.green
                      : isHighlight
                      ? styles.green
                      : styles.red
                  }
                >
                  {value}
                </span>
              </div>
            );
          })}
        </div>
      );
    });

  return (
    <div className={`${styles.table} ${styles.table_7} ${styles.ledger}`}>
      <div className={styles.table_header}>
        {ledgerColumns.map((column) => (
          <div key={column.id} className={styles.table_header_cell}>
            <span className={styles.table_header_name}>{column.name}</span>
          </div>
        ))}
      </div>
      <div className={styles.ledger_body}>
        {renderTableRows()}
        {renderEmiRows()}
      </div>
    </div>
  );
}

export default BookingsLedgerTable;
